import { useTranslation } from "react-i18next";


export type SettingsSectionKey =
  | "personal"
  | "work-experience"
  | "availability"
  | "payments"
  | "communication"
  | "terms"
  | "business-config"
  | "billing"
  | "language";

type Section = {
  key: SettingsSectionKey;
  labelKey: string;
  roles: Array<"worker" | "employer">;
  /** Hidden unless billing is visible (flag + per-account opt-in). */
  requiresBilling?: boolean;
};

const ALL_SECTIONS: Array<Section> = [
  { key: "personal", labelKey: "nav.sections.personal", roles: ["worker"] },
  {
    key: "work-experience",
    labelKey: "nav.sections.workExperience",
    roles: ["worker"],
  },
  {
    key: "availability",
    labelKey: "nav.sections.availability",
    roles: ["worker"],
  },
  {
    key: "business-config",
    labelKey: "nav.sections.businessConfig",
    roles: ["employer"],
  },
  // "payments" temporarily hidden — subscription/billing UX is being reworked.
  // Re-add here (and it will render via the existing PaymentsPanel case) when ready.
  // Employer subscription billing (plan, payment method, Stripe portal).
  // Only listed when billing is visible for this account (see options below).
  {
    key: "billing",
    labelKey: "nav.sections.billing",
    roles: ["employer"],
    requiresBilling: true,
  },
  {
    key: "communication",
    labelKey: "nav.sections.communication",
    roles: ["worker", "employer"],
  },
  {
    key: "terms",
    labelKey: "nav.sections.terms",
    roles: ["worker", "employer"],
  },
  {
    key: "language",
    labelKey: "nav.sections.language",
    roles: ["worker", "employer"],
  },
];

export function getSettingsSections(
  role: "worker" | "employer",
  options: { billingEnabled?: boolean } = {},
) {
  return ALL_SECTIONS.filter(
    (section) =>
      section.roles.includes(role) &&
      (!section.requiresBilling || options.billingEnabled === true),
  );
}

export function SettingsNav({
  selected,
  onSelect,
  role,
  billingEnabled = false,
}: {
  selected: SettingsSectionKey;
  onSelect: (key: SettingsSectionKey) => void;
  role: "worker" | "employer";
  billingEnabled?: boolean;
}) {
  const { t } = useTranslation("settings");
  const sections = getSettingsSections(role, { billingEnabled });
  return (
    <nav className="bg-white rounded-2xl shadow p-3 sm:p-6 flex flex-col w-[140px] sm:w-[200px] md:w-[280px] shrink-0">
      <h2 className="hidden sm:block text-base md:text-lg font-bold text-garnet mb-4 md:mb-6">
        {t("nav.title")}
      </h2>
      <ul className="flex flex-col gap-1 sm:gap-2">
        {sections.map((section) => (
          <li key={section.key}>
            <button
              className={`w-full text-left px-2 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3 rounded-lg transition-colors font-semibold text-xs sm:text-sm md:text-base leading-snug ${selected === section.key ? "bg-gold/40 text-garnet border-2 border-gold" : "hover:bg-accent/20 text-garnet/80 border-2 border-transparent"}`}
              onClick={() => onSelect(section.key)}
            >
              {t(section.labelKey)}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
